// On this page follows the reader, on a page of the docs: the section they
// are in is marked in the page's contents (aria-current), as the text mode
// marks it in cells (shared/docsview): the last heading above a mark a
// third of the way down the window, the mark going down to the window's
// foot over the page's last screenful, so that the last sections, which
// never come to the top, have their turn (shared/docsview/layout's Mark).
// A heading the reader went to (a link to it, or back) is theirs until
// they scroll. The sites' shell runs it for each page it shows (mode.ts),
// hotty's site for its web page (hotty/web/src/main.ts).

/** What follow gives its page: the reader's section, for a terminal to
 * open there, and a look again, when the page is shown again. */
export type Contents = { section(): string; queue(): void };

/** follow marks the page's contents (.toc) while shown says the page is
 * the one on screen, until signal, if one is given; null on a page
 * without contents. */
export function follow(shown: () => boolean, signal?: AbortSignal): Contents | null {
  const toc = document.querySelector<HTMLElement>(".toc");
  if (!toc) return null;
  const links: HTMLAnchorElement[] = [];
  const heads: HTMLElement[] = [];
  for (const a of toc.querySelectorAll<HTMLAnchorElement>('a[href^="#"]')) {
    const h = document.getElementById(decodeURIComponent(a.hash.slice(1)));
    if (h) {
      links.push(a);
      heads.push(h);
    }
  }
  if (!heads.length) return null;
  let pin: { at: number; y: number } | null = null; // the heading gone to, and where the window was then
  let here = -1;
  let queued = false;

  // The mark, from the window's top.
  function mark() {
    const rows = window.innerHeight;
    const end = document.documentElement.scrollHeight - rows;
    let m = rows / 3;
    if (end > 0) {
      const span = Math.min(rows, end);
      const left = Math.max(0, end - window.scrollY);
      if (left < span) m += ((rows - 1 - m) * (span - left)) / span;
    }
    return m;
  }
  function section() {
    // Firefox settles a pixel off where a link took it.
    if (pin && Math.abs(window.scrollY - pin.y) < 8) return pin.at;
    pin = null;
    const m = mark();
    let at = -1;
    for (let i = 0; i < heads.length && heads[i].getBoundingClientRect().top <= m; i++) at = i;
    return at;
  }
  function update() {
    queued = false;
    if (!shown()) return;
    const at = section();
    if (at === here) return;
    if (here >= 0) links[here].removeAttribute("aria-current");
    here = at;
    if (at < 0) return;
    links[at].setAttribute("aria-current", "true");
    // In view where the contents scroll, without scrolling the page.
    const box = toc!.getBoundingClientRect();
    const r = links[at].getBoundingClientRect();
    if (r.top < box.top || r.bottom > box.bottom) toc!.scrollTop += r.top - box.top - box.height / 3;
  }
  function queue() {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  }
  // The window has gone to the address's heading, if it names one.
  function went() {
    const id = decodeURIComponent(location.hash.slice(1));
    const at = id ? heads.findIndex((h) => h.id === id) : -1;
    pin = at >= 0 ? { at, y: window.scrollY } : null;
    queue();
  }
  addEventListener("scroll", queue, { passive: true, signal });
  addEventListener("resize", queue, { signal });
  addEventListener("hashchange", went, { signal });
  addEventListener("load", went, { signal });
  went();
  // Where the reader is: the heading of their section, or none at the
  // page's top.
  return { section: () => (window.scrollY < 4 ? "" : (heads[section()]?.id ?? "")), queue };
}
